import { useMemo } from "react";
import type {
  BaseEntity,
  EntityHooks,
  SimpleTableConfig,
} from "../../../UniversalCRMTable/types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import type { PaginationModeContextValue } from "../../contexts/types";
import { useUniversalEntityPageData } from "../useUniversalEntityPageData";
import { normalizeCurrentFilters } from "./normalize-current-filters";

/**
 * Composition hook for Data Fetching and Processing
 * Orchestrates data fetching and filter normalization
 *
 * Job Duty: Orchestrate data fetching and filter normalization
 */
export function useCompositionDataFetching<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  hooks: EntityHooks<T>,
  filterPreset: "all" | "user" | undefined,
  customFilters: Record<string, unknown>,
  appUser: AuthenticatedUser,
  enableFTS: boolean,
  paginationModeState: PaginationModeContextValue
) {
  // Memoize customFilters to prevent unnecessary recalculations
  const memoizedCustomFilters = useMemo(
    () => customFilters,
    [customFilters]
  );

  // Fetch and process data
  const data = useUniversalEntityPageData(
    {
      entityName,
      config,
      hooks,
      filterPreset,
      customFilters: memoizedCustomFilters,
      appUser,
      enableFTS,
      paginationModeState,
    },
    appUser
  );

  const { navigation, entityState, filterState, paginationState, dataState, mutations } = data;

  // Normalize advancedFilters from array to object format for SerializedViewState
  const normalizedCurrentFilters = useMemo(
    () => normalizeCurrentFilters(filterState.currentFilters),
    [filterState.currentFilters]
  );

  // Create serializeFilters function for saved views
  const serializeFilters = useMemo(() => {
    return () => normalizedCurrentFilters;
  }, [normalizedCurrentFilters]);

  // Enhance filterState with serializeFilters and the normalized currentFilters.
  // The upstream `filterState.currentFilters` is a FilterContext-derived shape
  // (Record-based filters, AdvancedFilterGroup[]). Replacing it here lets the
  // downstream context contract use the canonical SerializedViewState shape
  // without runtime divergence between filterState.currentFilters and the
  // separate normalizedCurrentFilters channel.
  const enhancedFilterState = useMemo(
    () => ({
      ...filterState,
      currentFilters: normalizedCurrentFilters,
      serializeFilters,
    }),
    [filterState, normalizedCurrentFilters, serializeFilters]
  );

  return {
    data,
    navigation,
    entityState,
    filterState: enhancedFilterState,
    paginationState,
    dataState,
    mutations,
    normalizedCurrentFilters,
  };
}
